import React from 'react';
import {
  Button,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
  Box,
} from '@mui/material';
import {
  Elements,
  CardElement,
  useStripe,
  useElements,
} from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';
import firebase from '../utilities/firebase.js';
import RequestManager from '../utilities/requestManager.js';
import { StateManager } from '../utilities/stateManager.js';
import constants from '../utilities/constants';
import { StackedDialog } from '../utilities/ModalStackContext';

const stripeKey = process.env.NODE_ENV === 'development'
  ? 'pk_test_TKhGrmB7K2upBFGy2x7KSP6e0002WFRtWs'
  : 'pk_live_ItQHT80SBCB2LpW3zkZW84sP00RnGbxWjs';
const stripePromise = loadStripe(stripeKey);

const devtype = process.env.NODE_ENV === 'development' ? 'dev' : 'prod';

const filterCustomerFields = (cust, keysToKeep) => {
  return Object.keys(cust || {}).reduce((result, key) => {
    if (keysToKeep.includes(key) && cust[key]) result[key] = cust[key];
    return result;
  }, {});
};

const ensureStripeCustomer = async (customer) => {
  if (customer.stripe_id) return customer.stripe_id;
  const cust_vars = filterCustomerFields(customer, ['email']);
  // Stripe rejects emails with stray whitespace as invalid.
  if (cust_vars.email) cust_vars.email = cust_vars.email.trim();
  cust_vars.name = customer.customerName
    ? customer.customerName
    : `${customer.first_name || ''} ${customer.last_name || ''}`.trim();
  cust_vars.devtype = devtype;
  const stripe_cust = await RequestManager.post({
    function: 'makeStripeCustomer',
    variables: { params: cust_vars },
  });
  if (!stripe_cust || stripe_cust.success === false || !stripe_cust.id) return null;
  if (constants.isValidCustomerId(customer.id)) {
    await firebase.firestore().doc(`customers/${customer.id}`).update({ stripe_id: stripe_cust.id });
  }
  return stripe_cust.id;
};

const CardSetupForm = ({ customer, onSuccess, onClose }) => {
  const stripe = useStripe();
  const elements = useElements();
  const [error, setError] = React.useState(null);
  const [submitting, setSubmitting] = React.useState(false);

  const handleSubmit = async () => {
    if (!stripe || !elements) return;
    setError(null);
    setSubmitting(true);
    StateManager.setLoading(true);

    const stripeId = await ensureStripeCustomer(customer);
    if (!stripeId) {
      setSubmitting(false);
      StateManager.setLoading(false);
      setError('Could not create or find Stripe customer for this profile.');
      return;
    }

    const intentRes = await RequestManager.post({
      function: 'makeSetupIntent',
      variables: { params: { customer: stripeId, devtype } },
    });

    if (!intentRes || !intentRes.success || !intentRes.client_secret) {
      setSubmitting(false);
      StateManager.setLoading(false);
      setError(intentRes && intentRes.message ? intentRes.message : 'Could not start card setup.');
      return;
    }

    const cardElement = elements.getElement(CardElement);
    const billingName = customer.customerName
      ? customer.customerName
      : `${customer.first_name || ''} ${customer.last_name || ''}`.trim();

    const result = await stripe.confirmCardSetup(intentRes.client_secret, {
      payment_method: {
        card: cardElement,
        billing_details: {
          name: billingName || undefined,
          email: (customer.email || '').trim() || undefined,
        },
      },
    });

    setSubmitting(false);
    StateManager.setLoading(false);

    if (result.error) {
      setError(result.error.message);
      return;
    }
    if (result.setupIntent && result.setupIntent.status === 'succeeded') {
      StateManager.setAlertAndOpen('Card saved.', 'success');
      if (onSuccess) onSuccess({ setupIntent: result.setupIntent, stripeId });
      return;
    }
    setError('Card setup did not complete. Try again.');
  };

  return (
    <>
      <DialogContent>
        <Typography variant="body2" sx={{ mb: 2 }}>
          Enter the customer's card. It will be saved on file and can be charged later.
        </Typography>
        <Box sx={{ p: 2, border: '1px solid #ccc', borderRadius: 1 }}>
          <CardElement options={{ hidePostalCode: false }} />
        </Box>
        {error && (
          <Typography variant="body2" color="error" sx={{ mt: 2 }}>
            {error}
          </Typography>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={submitting}>Cancel</Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          color="primary"
          disabled={!stripe || submitting}
        >
          {submitting ? 'Saving…' : 'Save card'}
        </Button>
      </DialogActions>
    </>
  );
};

export default function AddCardModal({ customer, open, onClose, onSuccess }) {
  return (
    <StackedDialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>Add card on file</DialogTitle>
      <Elements stripe={stripePromise}>
        <CardSetupForm customer={customer} onSuccess={onSuccess} onClose={onClose} />
      </Elements>
    </StackedDialog>
  );
}
